import {
  Description,
  Dialog,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react'
import { confirmResetPassword } from 'aws-amplify/auth'
import { ErrorMessage, Field, Form, Formik } from 'formik'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import Button from '../components/Button/Button'
import Password from '../components/Password'
import { PasswordConfirmationSchema, PasswordSchema } from '../utils/schemas'

export const ForgotPasswordConfirmation = () => {
  const [hasError, setHasError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [loading, setLoading] = useState(false)

  const navigate = useNavigate()

  const ValidationSchema = Yup.object().shape({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: PasswordSchema,
    password_confirmation: PasswordConfirmationSchema('password'),
    code: Yup.string()
      .length(6, 'Your code should contain 6 characters')
      .required('Code is required'),
  })

  interface ResetPassword {
    email: string
    code: string
    password: string
    password_confirmation: string
  }

  const resetPassword = async ({
    email,
    code,
    password,
    password_confirmation: passwordConfirmation,
  }: ResetPassword) => {
    if (password !== passwordConfirmation) {
      setLoading(false)
      setErrorMessage('Passwords do not match')
      setHasError(true)
    }
    setLoading(true)
    try {
      await confirmResetPassword({
        username: email,
        newPassword: password,
        confirmationCode: code,
      })
      navigate('/login')
    } catch (e: any) {
      setErrorMessage(e.message)
      setHasError(true)
      setLoading(false)
    }
  }

  return (
    <>
      <Dialog
        open={hasError}
        onClose={() => setHasError(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" />
        <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
          <DialogPanel className="mx-auto max-w-sm rounded-lg p-10 bg-primary-50">
            <DialogTitle>Error while logging in</DialogTitle>
            <Description className="py-4">{errorMessage}</Description>

            <button
              className="ircam-button button button-regular button-dark block mx-auto px-4 py-2"
              type="button"
              onClick={() => setHasError(false)}
            >
              <div className="button-label">OK</div>
            </button>
          </DialogPanel>
        </div>
      </Dialog>
      <div className="w-4/6 mx-auto mt-28">
        <h1 className="text-4xl text-center my-8 font-light">
          Forgot your password ?
        </h1>
        <Formik
          onSubmit={resetPassword}
          validationSchema={ValidationSchema}
          initialValues={{
            email: '',
            code: '',
            password: '',
            password_confirmation: '',
          }}
        >
          <Form>
            <div className="w-full my-4">
              <div className="my-8">
                <label htmlFor="email" className="text-sm">
                  Email address
                  <Field
                    type="text"
                    name="email"
                    id="email"
                    className="w-full ircam-input"
                  />
                  <ErrorMessage
                    name="email"
                    component="span"
                    className="text-ircamprimary-accent"
                  />
                </label>
              </div>

              <div className="my-8">
                <label htmlFor="code" className="text-sm">
                  Code
                  <Field
                    type="text"
                    name="code"
                    id="code"
                    className="w-full ircam-input"
                  />
                  <ErrorMessage
                    name="code"
                    component="span"
                    className="text-ircamprimary-accent"
                  />
                </label>
              </div>

              <div className="my-8">
                <label htmlFor="password" className="text-sm">
                  Password
                  <Field as={Password} name="password" id="password" />
                  <ErrorMessage
                    name="password"
                    component="span"
                    className="text-ircamprimary-accent"
                  />
                </label>
              </div>

              <div className="my-8">
                <label htmlFor="password_confirmation" className="text-sm">
                  Confirm your password
                  <Field
                    as={Password}
                    name="password_confirmation"
                    id="password_confirmation"
                  />
                  <ErrorMessage
                    name="password_confirmation"
                    component="span"
                    className="text-ircamprimary-accent"
                  />
                </label>
              </div>
              <div className="my-2">
                <button
                  type="submit"
                  className="w-full button button-large button-dark py-2 px-4"
                  disabled={loading}
                >
                  <div className="button button-label">Reset password</div>
                </button>
              </div>
              <div className="my-2">
                <Button
                  as={Link}
                  to="/login"
                  label="Cancel"
                  dark={false}
                  className="w-full my-8 py-2 text-center"
                />
              </div>
            </div>
          </Form>
        </Formik>
      </div>
    </>
  )
}
